/**
 * The responsibility of this custom hook is to manage event description and related data.
 *
 * @returns {Object} An object containing formik methods, state variables, and utility functions for managing event description.
 *
 * @description
 * This hook manages the event description form with the following steps:
 * 1. Initializes state variables and Redux selectors
 * 2. Sets up effect to manage edit button visibility based on event status
 * 3. Defines utility function to check for form changes
 * 4. Configures Formik with initial values, validation schema, and submit handler
 * 5. Implements form submission logic with error handling
 * 6. Provides utility functions for tag management and input formatting
 *
 * The form submission:
 * - Validates required fields
 * - Formats tags data properly for API submission
 * - Dispatches update action only if data has changed
 * - Handles API errors with appropriate messaging
 * - Advances to next tab on successful submission
 */

import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { isEqual } from 'lodash';
import { createFilterOptions } from '@mui/material/Autocomplete';
import { updateEventById, nextTab } from '../../../../../../app/store/slices/event/event';
import { errorToast } from '../../../../../../utils/toasts/toasts';
import { useEventStatus } from '../../../../../../hooks/event-hooks/useEventStatus';

// Quill toolbar modules
const toolbarOptions = [
    [{ header: [1, 2, 3, 4, 5, 6, false] }],
    ['bold', 'italic', 'underline', 'strike'],
    ['blockquote', 'code-block'],
    ['link', 'image'],
    [{ list: 'ordered' }, { list: 'bullet' }],
    [{ indent: '-1' }, { indent: '+1' }],
    [{ color: [] }, { background: [] }],
    [{ font: [] }],
    [{ align: [] }],
    ['clean'] // Clear the formatting button
];

// Filter options for tags
const filter = createFilterOptions();

export default function useEventDescription() {
    const dispatch = useDispatch();
    const { eventData } = useSelector((state) => state.event);
    const { user } = useSelector((state) => state.auth);
    const [isEditModeOn, setIsEditModeOn] = useState(true);
    const [shouldShowEditButton, setShouldShowEditButton] = useState(false);
    const [loading, setLoading] = useState(false);

    const { isEventDrafted, isDescriptionTabCompleted, isEventLive } = useEventStatus();

    useEffect(() => {
        const shouldShow = isEventDrafted && isDescriptionTabCompleted;
        setShouldShowEditButton(shouldShow);

        if (eventData?.publish || isEventLive) {
            // If published → always false
            setIsEditModeOn(false);
        } else if (!isDescriptionTabCompleted || !isEventDrafted) {
            // If not completed OR not drafted → allow editing
            setIsEditModeOn(true);
        } else {
            // Both drafted & completed → disable edit mode
            setIsEditModeOn(false);
        }
    }, [isEventDrafted, isEventLive, isDescriptionTabCompleted, eventData?.publish]);

    function hasChanges(values, originalData) {
        const cleanedValues = {
            faqs: values.faqs,
            termsAndCondition: values.termsAndCondition,
            description: values.description,
            creatorMobileNumber: values.creatorMobileNumber,
            tags: values.tags.map((tag) => ({
                id: tag.id || 4, // Default to 4 for custom tags
                value: tag.value || tag.name || tag.inputValue || ''
            }))
        };

        const cleanedOriginalData = {
            faqs: originalData?.faq || '',
            termsAndCondition: originalData?.terms_and_conditions || '',
            description: originalData?.description || '',
            creatorMobileNumber: originalData?.emergency_contact_number || '',
            tags:
                originalData?.tags?.map((tag) => ({
                    id: tag.id || 4, // Default to 4 for custom tags
                    value: tag.value || tag.name || ''
                })) || []
        };

        return !isEqual(cleanedValues, cleanedOriginalData);
    }

    const initialValues = {
        tags:
            eventData?.tags?.map((tag) => ({
                id: tag.id,
                name: tag.name,
                value: tag.custom_value || tag.name // Use custom_value if available, otherwise name
            })) || [],
        faqs: eventData?.faq || '',
        description: eventData?.description || '',
        termsAndCondition: eventData?.terms_and_conditions || '',
        creatorMobileNumber: eventData?.emergency_contact_number || ''
    };

    const validationSchema = Yup.object({
        faqs: Yup.string(),
        termsAndCondition: Yup.string(),
        tags: Yup.array().max(10, 'Maximum 10 tags are allowed'),
        description: Yup.string(),
        creatorMobileNumber: Yup.string()
    });

    const formik = useFormik({
        initialValues,
        validationSchema,
        onSubmit: async (values) => {
            try {
                setLoading(true);
                if (!values.description) {
                    errorToast('Event description is required');
                    return;
                }

                const payload = {
                    step: 'description',
                    user_id: user?.id,
                    description: values.description,
                    faq: values.faqs,
                    emergency_contact_number: values.creatorMobileNumber,
                    terms_and_conditions: values.termsAndCondition,
                    tags: values.tags.map((tag) => {
                        if (tag.id && !tag.isUserCreated) {
                            return { id: tag.id, value: tag.value || null };
                        }

                        // For custom tags (created by user)
                        let customValue;
                        if (typeof tag === 'string') {
                            customValue = tag;
                        } else if (tag.inputValue) {
                            customValue = tag.inputValue;
                        } else if (tag.isUserCreated && tag.name) {
                            // Extract content from "Add "content"" format
                            const match = tag.name.match(/Add "([^"]+)"/);
                            customValue = match ? match[1] : tag.name;
                        }

                        // Return custom tag with id: 4 and user value
                        return { id: 4, value: customValue };
                    })
                };

                // console.log("Payload for description:", payload);

                if (hasChanges(values, eventData)) {
                    await dispatch(
                        updateEventById({
                            eventId: eventData?.eventId || 70,
                            eventData: payload
                        })
                    ).unwrap();
                }

                dispatch(nextTab());
            } catch (error) {
                errorToast(error?.data?.message || 'Failed to update event description');
            } finally {
                setLoading(false);
            }
        }
    });

    // console.log("Intial Values : ", initialValues);

    // function to handle tags selection as well as creation of new tags
    function handleTagsChange(event, newValue) {
        if (typeof newValue === 'string') {
            formik.setFieldValue('tags', newValue);
        } else if (newValue && newValue.inputValue) {
            formik.setFieldValue('tags', newValue.inputValue);
        } else {
            formik.setFieldValue('tags', newValue);
        }
    }

    // function to handle filter options for tags based on user input
    function handleFilterOptions(options, params) {
        const filtered = filter(options, params);
        const { inputValue } = params;

        // Suggest the creation of a new value
        const isExisting = options.some((option) => inputValue === option.name);
        if (inputValue !== '' && !isExisting) {
            filtered.push({
                inputValue,
                name: `Add "${inputValue}"`,
                isUserCreated: true
            });
        }

        return filtered;
    }

    // function to handle the label for the tags to be displayed in the autocomplete
    function getOptionLabel(option) {
        if (option.id === 4 && option.value) {
            return option.value;
        }

        // Value selected with enter, right from the input
        if (typeof option === 'string') {
            return option;
        }

        // Add "zzz" option created dynamically
        if (option.inputValue) {
            return option.inputValue;
        }

        // Regular option
        return option.name;
    }

    // function to handle the mobile number input and format it
    function handleMobileNumberChange(event) {
        formik.setFieldValue(
            'creatorMobileNumber',
            event.target.value.replace(/[^\d]+/g, '').slice(0, 10)
        );
    }

    return {
        loading,
        formik,
        isEditModeOn,
        setIsEditModeOn,
        toolbarOptions,
        getOptionLabel,
        handleTagsChange,
        handleFilterOptions,
        handleMobileNumberChange,
        shouldShowEditButton
    };
}
